import type { FC } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { LinkButton } from '@atlaskit/button/new';
import Link from '@atlaskit/link';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import type { PublicLinkPageStatus } from '@confluence/public-links/entry-points/PublicLinkPageQuery.ts';
import { usePublicLinkPrivilegeData } from '@confluence/public-links/entry-points/usePublicLinkPrivilegeData.ts';
import {
	DATA_SECURITY_POLICY_DOCUMENTATION,
	SET_UP_AND_MANAGE_DOCUMENTATION,
} from '@confluence/public-links/entry-points/constants.ts';

const i18n = defineMessages({
	linkAllowInAtlassianAdministration: {
		id: 'share-and-restrict-dialog.public-links.link-allow-in-atlassian-administration',
		defaultMessage: 'Allow in Atlassian Administration',
		description: 'CTA to allow public links in Atlassian Administration',
	},
	linkAllowInSpaceSettings: {
		id: 'share-and-restrict-dialog.public-links.link-allow-in-space-settings',
		defaultMessage: 'Allow in Space settings',
		description: 'CTA to allow public links in Space settings',
	},
	linkAllowInSettings: {
		id: 'share-and-restrict-dialog.public-links.link-allow-in-settings',
		defaultMessage: 'Allow in settings',
		description: 'CTA to allow public links in settings',
	},
	learnMore: {
		id: 'share-and-restrict-dialog.public-links.learn-more-link',
		defaultMessage: 'Learn more about public links',
		description: 'Link to learn more about public links',
	},
	bodyEmpowered: {
		id: 'share-and-restrict-dialog.public-links.body-empowered',
		defaultMessage:
			'If allowed, anyone who can view this content can share it with anyone on the internet without giving them access to Confluence.',
		description: 'Body text when the user is authorized to enable a public link use',
	},
	bodyUnempowered: {
		id: 'share-and-restrict-dialog.public-links.body-unempowered',
		defaultMessage:
			'When allowed, you can share this content with anyone on the internet without giving them access to Confluence.',
		description: 'Body text when the user is not authorized to enable a public link use',
	},
	titleEmpowered: {
		id: 'share-and-restrict-dialog.public-links.title-empowered',
		defaultMessage: 'This public link isn’t allowed',
		description: 'Title when the user is authorized to enable a public link use',
	},
	titleUnempowered: {
		id: 'share-and-restrict-dialog.public-links.title-unempowered',
		defaultMessage: 'Your organization is preventing the use of this public link',
		description: 'Title when the user is not authorized to enable a public link use',
	},
});

export const PublicLinkDisabledBody: FC<{
	publicLinkStatus: PublicLinkPageStatus | null;
}> = ({ publicLinkStatus }) => {
	const { formatMessage } = useIntl();
	const { isEmpowered, empoweredUserSettingsLinkHref, accessLevel, isSiteAdmin, error } =
		usePublicLinkPrivilegeData({ publicLinkStatus: publicLinkStatus ?? null });

	const learnMoreLinkHref =
		accessLevel === 'org' && isSiteAdmin
			? DATA_SECURITY_POLICY_DOCUMENTATION
			: SET_UP_AND_MANAGE_DOCUMENTATION;

	const getSettingsButtonText = useCallback(() => {
		switch (accessLevel) {
			case 'org':
				return formatMessage(i18n.linkAllowInAtlassianAdministration);
			case 'space':
				return formatMessage(i18n.linkAllowInSpaceSettings);
			default:
				return formatMessage(i18n.linkAllowInSettings);
		}
	}, [accessLevel, formatMessage]);

	const getButtons = useCallback(() => {
		switch (isEmpowered) {
			case false:
				return {
					primary: {
						text: formatMessage(i18n.learnMore),
						href: learnMoreLinkHref,
					},
					secondary: null,
				};
			default:
				return {
					primary: {
						text: getSettingsButtonText(),
						href: empoweredUserSettingsLinkHref,
					},
					secondary: {
						text: formatMessage(i18n.learnMore),
						href: learnMoreLinkHref,
					},
				};
		}
	}, [
		empoweredUserSettingsLinkHref,
		getSettingsButtonText,
		learnMoreLinkHref,
		formatMessage,
		isEmpowered,
	]);

	const { primary, secondary } = getButtons();

	return (
		<>
			{error && <ErrorDisplay error={error} />}
			<SectionMessage
				testId="public-link-disabled-body"
				appearance="information"
				title={formatMessage(isEmpowered ? i18n.titleEmpowered : i18n.titleUnempowered)}
				actions={
					<Inline space="space.150" alignBlock="center">
						{primary && (
							<LinkButton appearance="primary" href={primary.href ?? '#'} target="_blank">
								{primary.text}
							</LinkButton>
						)}
						{secondary && (
							<Link href={secondary.href} target="_blank">
								{secondary.text}
							</Link>
						)}
					</Inline>
				}
			>
				<Text>{formatMessage(isEmpowered ? i18n.bodyEmpowered : i18n.bodyUnempowered)}</Text>
			</SectionMessage>
		</>
	);
};
